import React, { memo, useCallback, useEffect, useMemo } from "react";
import {
  Card,
  ContentSwitcher,
  DatePicker,
  Dropdown,
  Iconography,
  IconographySizes,
  InlineEditor,
  Select,
  TextField,
} from "@hid-galaxy-ui/galaxy-react";
import type { StylesConfig } from "react-select";
import IToolBarConfig from "@hid-galaxy-ui/galaxy-react/components/InlineEditor/IToolbarConfig";
import {
  usePartnerForm,
  PartnerFormData,
} from "../../../../hooks/usePartnerForm";
import {
  Select as MuiSelect,
  OutlinedInput,
  Checkbox,
  ListItemText,
  MenuItem,
  Box,
  Chip,
  Autocomplete,
  TextField as MuiTextField,
  Popper,
} from "@mui/material";
import { notesToolBarConfig } from "../../../partner/dashboard/projects/project-details/validation/components/NotesSection";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import { getUsersByTypeSagaAction } from "../../../../redux/saga/user/saga-actions";
import "./PartnerDetailForm.scss";

const CREATE_PARTNER_MODAL_Z_INDEX = 10000;
const PARTNER_MODAL_DROPDOWN_OPTION_HOVER_BG = "#e6e6e6";

type DropdownOption = { label: string; value: string };

const modalDropdownMenuStyles: StylesConfig<DropdownOption, false> = {
  menuPortal: (base) => ({
    ...base,
    zIndex: CREATE_PARTNER_MODAL_Z_INDEX,
  }),
  menu: (base) => ({
    ...base,
    zIndex: CREATE_PARTNER_MODAL_Z_INDEX + 1,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? PARTNER_MODAL_DROPDOWN_OPTION_HOVER_BG
      : state.isSelected
        ? base.backgroundColor
        : "transparent",
    color: base.color,
    ":active": {
      backgroundColor: PARTNER_MODAL_DROPDOWN_OPTION_HOVER_BG,
    },
  }),
};

const findSelectedOption = (
  options: DropdownOption[],
  value: string
): DropdownOption | null => options.find((option) => option.value === value) || null;

const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({
  label,
  required = false,
}) => (
  <label className="field-label">
    {label}
    {required ? <span aria-hidden="true">*</span> : null}
  </label>
);

const ModalAutocompletePopper = (
  props: React.ComponentProps<typeof Popper>
) => (
  <Popper
    {...props}
    style={{ ...props.style, zIndex: CREATE_PARTNER_MODAL_Z_INDEX }}
    placement="bottom-start"
  />
);

interface PartnerDetailFormInterface {
  partnerDetails: PartnerFormData;
  setPartnerDetails: React.Dispatch<React.SetStateAction<PartnerFormData>>;
  layout?: "default" | "modal";
  selectedCountryCode?: { label: string; value: string } | null;
  selectedCountry?: { label: string; value: string } | null;
  setSelectedCountryCode?: React.Dispatch<
    React.SetStateAction<{ label: string; value: string } | null>
  >;
  setSelectedCountry?: React.Dispatch<
    React.SetStateAction<{ label: string; value: string } | null>
  >;
}

const customToolBarConfig: IToolBarConfig = {
  headingConfig: {
    isDisplayHeading: true,
    headings: [false, 1, 2, 3, 4, 5, 6],
  },
  fontStyleConfig: {
    isDisplayBold: true,
    isDisplayItalic: true,
    isDisplayUnderline: true,
    isDisplayStrike: true,
  },
  indentConfig: {
    isDisplayOrdered: true,
    isDisplayBullet: true,
    isDisplayIndent: false,
  },
  colorConfig: {
    isDisplayBackground: false,
    isDisplayColor: false,
  },
  alignmentConfig: {
    isDisplayJustify: false,
    isDisplayCenter: false,
    isDisplayRight: false,
  },
  miscConfig: {
    isDisplayImage: false,
    isDisplayLink: false,
    isDisplayCodeblock: false,
    isDisplayBlockquote: false,
  },
  buttonConfig: {
    buttonLabel: "",
    displayPostButton: false,
    isRequired: false,
  },
};

// Memoized form field components
const PartnerNameField = memo(
  ({
    value,
    onChange,
    variant = "default",
    required = false,
  }: {
    value: string;
    onChange: (value: string) => void;
    variant?: "default" | "modal";
    required?: boolean;
  }) => (
    <div className="label-field-container">
      <FieldLabel
        label={
          variant === "modal" ? "Partner company name" : "Partner Company Name"
        }
        required={required}
      />
      <TextField
        label=""
        id="name"
        name="name"
        placeholder={
          variant === "modal" ? "Partner company name" : "Partner Company Name"
        }
        value={value}
        theme="dark"
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    </div>
  )
);

const DescriptionField = memo(
  ({
    value,
    onChange,
  }: {
    value: string;
    onChange: (value: string) => void;
  }) => (
    <div className="inline-input-wrapper">
      <label className="field-label">Description</label>
      <InlineEditor
        id="description"
        placeholder="Description"
        toolBarConfig={customToolBarConfig}
        editorData={value}
        handleOnChange={onChange}
      />
    </div>
  )
);

const HighLevelScenarioField = memo(
  ({
    value,
    onChange,
  }: {
    value: string;
    onChange: (value: string) => void;
  }) => (
    <div className="partner-high-level-scenario">
      <FieldLabel label="Description" />
      <InlineEditor
        id="highLevelScenario"
        placeholder="Description"
        toolBarConfig={notesToolBarConfig}
        editorData={value}
        handleOnChange={onChange}
      />
    </div>
  )
);

const PartnerTypeField = memo(
  ({
    partnerTypes,
    selectedTypes,
    onTypeClick,
  }: {
    partnerTypes: { value: string }[];
    selectedTypes: string[];
    onTypeClick: (value: string) => void;
  }) => (
    <div className="partner-type-input-wrapper">
      <label className="field-label">Partner Type</label>
      <div className="partner-type-container">
        {partnerTypes.map((type) => {
          const isSelected = selectedTypes.includes(type.value);
          return (
            <Card
              variant={"selectable"}
              selected={isSelected}
              cardSelectedIcon={<Iconography icon="none" />}
              value={type.value}
              key={type.value}
              onClick={(e) => onTypeClick(type.value)}
            >
              <div className="card-content">
                <span>{type.value}</span>
                {isSelected ? (
                  <Iconography
                    icon="squareCheck"
                    size={IconographySizes.Medium}
                  />
                ) : (
                  <Iconography icon="square" size={IconographySizes.Medium} />
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  )
);

const SearchableMultiSelectField = memo(
  ({
    label,
    required = false,
    options,
    selectedValues,
    onChange,
    placeholder,
    id,
  }: {
    label: string;
    required?: boolean;
    options: string[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
    placeholder: string;
    id: string;
  }) => (
    <div className="label-field-container partner-searchable-multiselect">
      <FieldLabel label={label} required={required} />
      <Autocomplete
        id={id}
        multiple
        disableCloseOnSelect
        options={options}
        value={selectedValues}
        onChange={(_, value) => onChange(value)}
        PopperComponent={ModalAutocompletePopper}
        popupIcon={
          <Iconography icon="angleDown" size={IconographySizes.Small} />
        }
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox size="small" checked={selected} />
            {option}
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              label={option}
              {...getTagProps({ index })}
              key={option}
            />
          ))
        }
        renderInput={(params) => (
          <MuiTextField
            {...params}
            placeholder={selectedValues.length ? "" : placeholder}
          />
        )}
      />
    </div>
  )
);

const PotentialField = memo(
  ({
    value,
    onChange,
    variant = "default",
  }: {
    value: string;
    onChange: (key: string) => void;
    variant?: "default" | "modal";
  }) => {
    const switcher = (
      <ContentSwitcher
        data={[
          { key: "Non-Prime", label: "Non-Prime" },
          { key: "Prime", label: "Prime" },
        ]}
        selected={value}
        onClick={(e, key) => onChange(key)}
      />
    );

    if (variant === "modal") {
      return (
        <div className="label-field-container partner-form-prime-toggle">
          <span
            className="field-label partner-form-prime-toggle__spacer"
            aria-hidden="true"
          >
            &nbsp;
          </span>
          {switcher}
        </div>
      );
    }

    return <div className="input-row-wrapper">{switcher}</div>;
  }
);

const AccountManagerEmailField = memo(
  ({
    value,
    onChange,
    onBlur,
    errorMsg,
    options,
    variant = "default",
    isLoading = false,
  }: {
    value: string;
    onChange: (value: string) => void;
    onBlur: () => void;
    errorMsg: string;
    options: DropdownOption[];
    variant?: "default" | "modal";
    isLoading?: boolean;
  }) => {
    const selected = findSelectedOption(options, value);
    const placeholder = isLoading
      ? "Loading HID users..."
      : "Search or select HID account manager";

    const handleSelect = (nextValue: string) => {
      onChange(nextValue);
      onBlur();
    };

    return (
      <div className="label-field-container partner-searchable-multiselect">
        {variant === "modal" ? (
          <FieldLabel label="HID Account Manager E-Mail ID" />
        ) : (
          <label className="field-label">HID Account Manager E-Mail ID</label>
        )}
        <Autocomplete
          id="accountManagerEmail"
          options={options}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          value={selected}
          loading={isLoading}
          disabled={isLoading}
          onChange={(_, data) => handleSelect(data?.value || "")}
          PopperComponent={
            variant === "modal" ? ModalAutocompletePopper : undefined
          }
          popupIcon={
            <Iconography icon="angleDown" size={IconographySizes.Small} />
          }
          noOptionsText="No HID users found"
          renderInput={(params) => (
            <MuiTextField
              {...params}
              placeholder={selected ? "" : placeholder}
              error={Boolean(errorMsg)}
              helperText={errorMsg || undefined}
            />
          )}
        />
      </div>
    );
  }
);

const EmailAndWebsiteFields = memo(
  ({
    emailValue,
    emailOnChange,
    emailOnBlur,
    emailStatus,
    emailErrorMsg,
    websiteValue,
    websiteOnChange,
  }: {
    emailValue: string;
    emailOnChange: (value: string) => void;
    emailOnBlur: () => void;
    emailStatus: any;
    emailErrorMsg: string;
    websiteValue: string;
    websiteOnChange: (value: string) => void;
  }) => (
    <div className="input-row-wrapper">
      <div className="input-col-wrapper">
        <div className="label-field-container">
          <label className="field-label">Partner E-Mail ID</label>
          <TextField
            {...emailStatus}
            helperText={emailErrorMsg}
            label=""
            id="email"
            name="email"
            placeholder="@organization.com"
            value={emailValue}
            theme="dark"
            onChange={(e) => emailOnChange(e.currentTarget.value)}
            onBlur={emailOnBlur}
          />
        </div>
      </div>
      <div className="input-col-wrapper">
        <div className="label-field-container">
          <label className="field-label">Website</label>
          <TextField
            label=""
            id="website"
            name="website"
            placeholder=""
            value={websiteValue}
            theme="dark"
            onChange={(e) => websiteOnChange(e.currentTarget.value)}
          />
        </div>
      </div>
    </div>
  )
);

const RegionAndRevenueFields = memo(
  ({
    regionValue,
    regionOnChange,
    regionOptions,
    revenueValue,
    revenueOnChange,
    revenueOptions,
  }: {
    regionValue: string;
    regionOnChange: (value: string) => void;
    regionOptions: { label: string; value: string }[];
    revenueValue: string;
    revenueOnChange: (value: string) => void;
    revenueOptions: { label: string; value: string }[];
  }) => (
    <div className="input-row-wrapper">
      <div className="input-col-wrapper">
        <div className="label-field-container">
          <label className="field-label">Region</label>
          <Select
            id="region"
            name="region"
            value={regionValue}
            onChange={(e: any) => regionOnChange(e.currentTarget.value)}
            options={regionOptions}
          />
        </div>
      </div>
      <div className="input-col-wrapper">
        <div className="label-field-container">
          <label className="field-label">Revenue Stream</label>
          <Select
            id="revenueStream"
            name="revenueStream"
            value={revenueValue}
            onChange={(e: any) => revenueOnChange(e.currentTarget.value)}
            options={revenueOptions}
          />
        </div>
      </div>
    </div>
  )
);

const CountryAndStateFields = memo(
  ({
    countryValue,
    countryOnSelect,
    countryOptions,
    countrySelected,
    stateValue,
    stateOnChange,
  }: {
    countryValue: string;
    countryOnSelect: (data: { label: string; value: string }) => void;
    countryOptions: { label: string; value: string }[];
    countrySelected: { label: string; value: string } | null;
    stateValue: string;
    stateOnChange: (value: string) => void;
  }) => (
    <div className="input-row-wrapper">
      <div className="input-col-wrapper">
        <div className="label-field-container">
          <label className="field-label">Country</label>
          <Dropdown
            label=""
            id="country"
            name="country"
            options={countryOptions}
            selected={countrySelected}
            onSelect={countryOnSelect}
            menuPortalTarget={document.body}
            isClearable={true}
          />
        </div>
      </div>
      <div className="input-col-wrapper">
        <div className="label-field-container">
          <label className="field-label">State</label>
          <TextField
            label=""
            id="state"
            name="state"
            placeholder=""
            value={stateValue}
            theme="dark"
            onChange={(e) => stateOnChange(e.currentTarget.value)}
          />
        </div>
      </div>
    </div>
  )
);

const PhoneAndAddressFields = memo(
  ({
    phoneValue,
    phoneOnChange,
    phoneOnBlur,
    phoneStatus,
    phoneErrorMsg,
    countryCodeValue,
    countryCodeOnSelect,
    countryCodeOptions,
    countryCodeSelected,
    addressValue,
    addressOnChange,
  }: {
    phoneValue: string;
    phoneOnChange: (value: string) => void;
    phoneOnBlur: () => void;
    phoneStatus: any;
    phoneErrorMsg: string;
    countryCodeValue: string;
    countryCodeOnSelect: (data: { label: string; value: string }) => void;
    countryCodeOptions: { label: string; value: string }[];
    countryCodeSelected: { label: string; value: string } | null;
    addressValue: string;
    addressOnChange: (value: string) => void;
  }) => (
    <div className="input-row-wrapper">
      <div className="input-col-wrapper">
        <div className="label-field-container">
          <label className="field-label">Phone Number</label>
          <div style={{ display: "flex", gap: "8px" }}>
            <div className="dropdown-wrapper">
              <Dropdown
                id="countryCode"
                label=""
                placeholder="Country Code"
                options={countryCodeOptions}
                selected={countryCodeSelected}
                onSelect={countryCodeOnSelect}
                menuPortalTarget={document.body}
                isClearable={true}
              />
            </div>
            <div className="phone-wrapper">
              <TextField
                {...phoneStatus}
                id="phoneNumber"
                name="phoneNumber"
                placeholder="Phone number"
                value={phoneValue}
                theme="dark"
                helperText={phoneErrorMsg}
                onChange={(e) => phoneOnChange(e.currentTarget.value)}
                onBlur={phoneOnBlur}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="input-col-wrapper">
        <div className="label-field-container">
          <label className="field-label">Street Address</label>
          <TextField
            label=""
            id="streetAddress"
            name="streetAddress"
            placeholder=""
            value={addressValue}
            theme="dark"
            onChange={(e) => addressOnChange(e.currentTarget.value)}
          />
        </div>
      </div>
    </div>
  )
);

const PartnerVerticalAndTierFields = memo(
  ({
    verticalValue,
    verticalOnChange,
    verticalOptions,
    tierValue,
    tierOnChange,
    tierOptions,
  }: {
    verticalValue: string[];
    verticalOnChange: (value: string[]) => void;
    verticalOptions: { label: string; value: string }[];
    tierValue: string;
    tierOnChange: (value: string) => void;
    tierOptions: { label: string; value: string }[];
  }) => (
    <div className="input-row-wrapper">
      <div className="label-field-container">
        <label className="field-label">Partner Vertical</label>
        <MuiSelect
          id="partnerVertical"
          multiple
          value={verticalValue}
          onChange={(e: any) => verticalOnChange(e.target.value)}
          input={<OutlinedInput />}
          renderValue={(selected) => {
            if (selected.length === 0) {
              return <em>Select Partner Vertical</em>;
            }
            return (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} />
                ))}
              </Box>
            );
          }}
        >
          {verticalOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox checked={verticalValue.includes(option.value)} />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </MuiSelect>
      </div>
      <div className="input-col-wrapper">
        <div className="label-field-container">
          <label className="field-label">Partner Tier</label>
          <Select
            id="partnerTier"
            name="partnerTier"
            value={tierValue}
            onChange={(e: any) => tierOnChange(e.currentTarget.value)}
            options={tierOptions}
          />
        </div>
      </div>
    </div>
  )
);

const RegistrationDateField = memo(
  ({
    value,
    onChange,
    variant = "default",
  }: {
    value: Date | null;
    onChange: (date: [Date | null, Date | null] | Date | null) => void;
    variant?: "default" | "modal";
  }) => {
    const field = (
      <div
        className={`label-field-container${
          variant === "modal" ? " partner-registration-date-field" : ""
        }`}
      >
        <FieldLabel label="Registration Date" />
        <DatePicker
          onChange={onChange}
          id="registrationDate"
          name="registrationDate"
          label=""
          format="dd/MM/yyyy"
          placeholder="dd/mm/yyyy"
          value={value}
        />
      </div>
    );

    if (variant === "modal") {
      return field;
    }

    return <div className="input-row-wrapper">{field}</div>;
  }
);

const FormSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="partner-form-section">
    <h3 className="partner-form-section__title">{title}</h3>
    <div className="partner-form-section__card">{children}</div>
  </section>
);

const PartnerDetailForm: React.FC<PartnerDetailFormInterface> = ({
  partnerDetails,
  setPartnerDetails,
  layout = "default",
  selectedCountryCode: propSelectedCountryCode,
  selectedCountry: propSelectedCountry,
  setSelectedCountryCode: propSetSelectedCountryCode,
  setSelectedCountry: propSetSelectedCountry,
}) => {
  const dispatch = useAppDispatch();
  const { userListHID, status: userStatus } = useAppSelector((state) => state.user);

  const {
    formData,
    setFormData,
    validationState,
    selectedCountryCode: hookSelectedCountryCode,
    selectedCountry: hookSelectedCountry,
    setSelectedCountryCode: hookSetSelectedCountryCode,
    setSelectedCountry: hookSetSelectedCountry,
    countryCodeOptions,
    countryOptions,
    regionOptions,
    revenueStreamOptions,
    partnerVerticalOptions,
    partnerTierOptions,
    partnerTypes,
    initializeLookupData,
    validateEmail,
    validateAccountManagerEmail,
    validatePhone,
    handleInputChange,
    handleCountryCodeSelect,
    handleCountrySelect,
    handlePartnerTypeClick,
    handlePotentialChange,
    handleDateChange,
  } = usePartnerForm(partnerDetails);

  const selectedCountryCode =
    hookSelectedCountryCode ?? propSelectedCountryCode;
  const selectedCountry = hookSelectedCountry ?? propSelectedCountry;
  const setSelectedCountryCode =
    hookSetSelectedCountryCode ?? propSetSelectedCountryCode;
  const setSelectedCountry = hookSetSelectedCountry ?? propSetSelectedCountry;

  // Initialize lookup data
  useEffect(() => {
    initializeLookupData();
  }, [initializeLookupData]);

  useEffect(() => {
    if (userListHID.length === 0 && !userStatus.userListHIDLoading) {
      dispatch(getUsersByTypeSagaAction("HID"));
    }
  }, [dispatch, userListHID.length, userStatus.userListHIDLoading]);

  // Sync form data with parent component
  useEffect(() => {
    setPartnerDetails(formData);
  }, [formData, setPartnerDetails]);

  // Set default values
  useEffect(() => {
    if (!formData.revenueStream && revenueStreamOptions.length > 0) {
      handleInputChange("revenueStream", revenueStreamOptions[0].value);
    }
  }, [revenueStreamOptions, formData.revenueStream, handleInputChange]);

  useEffect(() => {
    if (!formData.partnerTier && partnerTierOptions.length > 0) {
      handleInputChange("partnerTier", partnerTierOptions[0].value);
    }
  }, [partnerTierOptions, formData.partnerTier, handleInputChange]);

  // Clean up description
  useEffect(() => {
    if (formData.description === "<p><br></p>") {
      handleInputChange("description", "");
    }
  }, [formData.description, handleInputChange]);

  // Validation handlers
  const handleEmailBlur = useCallback(() => {
    validateEmail(formData.email);
  }, [formData.email, validateEmail]);

  const handleAccountManagerEmailBlur = useCallback(() => {
    validateAccountManagerEmail(formData.accountManagerEmail);
  }, [formData.accountManagerEmail, validateAccountManagerEmail]);

  const handlePhoneBlur = useCallback(() => {
    validatePhone(formData.phoneNumber, formData.countryCode);
  }, [formData.phoneNumber, formData.countryCode, validatePhone]);

  const partnerTypeOptions = useMemo(
    () => partnerTypes.map((type: { value: string }) => type.value),
    [partnerTypes]
  );

  const partnerVerticalOptionValues = useMemo(
    () =>
      partnerVerticalOptions.map(
        (option: { label: string; value: string }) => option.value
      ),
    [partnerVerticalOptions]
  );

  const accountManagerEmailOptions = useMemo(() => {
    const options = userListHID
      .filter((user) => user.email)
      .map((user) => {
        const name = `${user.first_name} ${user.last_name}`.trim();
        return {
          label: name ? `${name} (${user.email})` : user.email,
          value: user.email,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    if (
      formData.accountManagerEmail &&
      !options.some((option) => option.value === formData.accountManagerEmail)
    ) {
      options.unshift({
        label: formData.accountManagerEmail,
        value: formData.accountManagerEmail,
      });
    }

    return options;
  }, [userListHID, formData.accountManagerEmail]);

  const partnerTypeField =
    layout === "modal" ? (
      <SearchableMultiSelectField
        id="partnerTypeModal"
        label="Partner Type"
        options={partnerTypeOptions}
        selectedValues={formData.type}
        onChange={(value) => handleInputChange("type", value)}
        placeholder="Search partner type"
      />
    ) : (
      <PartnerTypeField
        partnerTypes={partnerTypes}
        selectedTypes={formData.type}
        onTypeClick={handlePartnerTypeClick}
      />
    );

  const formFields = (
    <>
      <div className="input-row-wrapper">
        <PartnerNameField
          required
          value={formData.name}
          onChange={(value) => handleInputChange("name", value)}
        />
      </div>

      <DescriptionField
        value={formData.description}
        onChange={(value) => handleInputChange("description", value)}
      />

      {layout === "default" && partnerTypeField}

      {layout === "default" && (
        <PotentialField
          value={formData.potential}
          onChange={handlePotentialChange}
        />
      )}

      {layout === "default" && (
        <div className="input-row-wrapper">
          <AccountManagerEmailField
            value={formData.accountManagerEmail}
            onChange={(value) => handleInputChange("accountManagerEmail", value)}
            onBlur={handleAccountManagerEmailBlur}
            errorMsg={validationState.errorMsg.accountManagerEmailMsg}
            options={accountManagerEmailOptions}
            isLoading={userStatus.userListHIDLoading}
          />
        </div>
      )}

      {layout === "default" && (
        <EmailAndWebsiteFields
          emailValue={formData.email}
          emailOnChange={(value) => handleInputChange("email", value)}
          emailOnBlur={handleEmailBlur}
          emailStatus={validationState.emailStatus}
          emailErrorMsg={validationState.errorMsg.emailMsg}
          websiteValue={formData.website}
          websiteOnChange={(value) => handleInputChange("website", value)}
        />
      )}

      {layout === "default" && (
        <RegionAndRevenueFields
          regionValue={formData.region}
          regionOnChange={(value) => handleInputChange("region", value)}
          regionOptions={regionOptions}
          revenueValue={formData.revenueStream}
          revenueOnChange={(value) => handleInputChange("revenueStream", value)}
          revenueOptions={revenueStreamOptions}
        />
      )}

      {layout === "default" && (
        <CountryAndStateFields
          countryValue={formData.country}
          countryOnSelect={handleCountrySelect}
          countryOptions={countryOptions}
          countrySelected={selectedCountry}
          stateValue={formData.state}
          stateOnChange={(value) => handleInputChange("state", value)}
        />
      )}

      {layout === "default" && (
        <PhoneAndAddressFields
          phoneValue={formData.phoneNumber}
          phoneOnChange={(value) => handleInputChange("phoneNumber", value)}
          phoneOnBlur={handlePhoneBlur}
          phoneStatus={validationState.phoneStatus}
          phoneErrorMsg={validationState.errorMsg.phoneMsg}
          countryCodeValue={formData.countryCode}
          countryCodeOnSelect={handleCountryCodeSelect}
          countryCodeOptions={countryCodeOptions}
          countryCodeSelected={selectedCountryCode}
          addressValue={formData.streetAddress}
          addressOnChange={(value) => handleInputChange("streetAddress", value)}
        />
      )}

      {layout === "default" && (
        <PartnerVerticalAndTierFields
          verticalValue={formData.partnerVertical}
          verticalOnChange={(value) =>
            handleInputChange("partnerVertical", value)
          }
          verticalOptions={partnerVerticalOptions}
          tierValue={formData.partnerTier}
          tierOnChange={(value) => handleInputChange("partnerTier", value)}
          tierOptions={partnerTierOptions}
        />
      )}

      {layout === "default" && (
        <RegistrationDateField
          value={formData.registrationDate}
          onChange={(date) => handleDateChange(date, "registrationDate")}
        />
      )}
    </>
  );

  if (layout === "modal") {
    return (
      <div className="partner-detail-form-container partner-detail-form-container--modal">
        <FormSection title="Basic Information">
          <PartnerNameField
            variant="modal"
            required
            value={formData.name}
            onChange={(value) => handleInputChange("name", value)}
          />
          <HighLevelScenarioField
            value={formData.description}
            onChange={(value) => handleInputChange("description", value)}
          />
        </FormSection>

        <FormSection title="Business Details">
          <div className="partner-form-grid">
            {partnerTypeField}
            <SearchableMultiSelectField
              id="partnerVerticalModal"
              label="Partner Vertical"
              options={partnerVerticalOptionValues}
              selectedValues={formData.partnerVertical}
              onChange={(value) => handleInputChange("partnerVertical", value)}
              placeholder="Search partner vertical"
            />
            <div className="label-field-container">
              <FieldLabel label="Revenue Stream" />
              <Dropdown
                label=""
                id="revenueStreamModal"
                name="revenueStream"
                options={revenueStreamOptions}
                selected={findSelectedOption(
                  revenueStreamOptions,
                  formData.revenueStream
                )}
                onSelect={(data) =>
                  handleInputChange("revenueStream", data?.value || "")
                }
                menuPortalTarget={document.body}
                styles={modalDropdownMenuStyles}
                isClearable={false}
              />
            </div>
            <PotentialField
              variant="modal"
              value={formData.potential}
              onChange={handlePotentialChange}
            />
          </div>
        </FormSection>

        <FormSection title="Contact Information">
          <div className="partner-form-grid">
            <div className="label-field-container">
              <FieldLabel label="Region" />
              <Dropdown
                label=""
                id="regionModal"
                name="region"
                options={regionOptions}
                selected={findSelectedOption(regionOptions, formData.region)}
                onSelect={(data) =>
                  handleInputChange("region", data?.value || "")
                }
                menuPortalTarget={document.body}
                styles={modalDropdownMenuStyles}
                isClearable={false}
              />
            </div>
            <div className="label-field-container">
              <FieldLabel label="Country" />
              <Dropdown
                label=""
                id="countryModal"
                name="country"
                placeholder="Enter country"
                options={countryOptions}
                selected={selectedCountry}
                onSelect={handleCountrySelect}
                menuPortalTarget={document.body}
                styles={modalDropdownMenuStyles}
                isClearable={true}
              />
            </div>
            <div className="label-field-container">
              <FieldLabel label="State" />
              <TextField
                label=""
                id="stateModal"
                name="state"
                placeholder=""
                value={formData.state}
                theme="dark"
                onChange={(e) => handleInputChange("state", e.currentTarget.value)}
              />
            </div>
            <div className="label-field-container">
              <FieldLabel label="Street Address" />
              <TextField
                label=""
                id="streetAddressModal"
                name="streetAddress"
                placeholder=""
                value={formData.streetAddress}
                theme="dark"
                onChange={(e) =>
                  handleInputChange("streetAddress", e.currentTarget.value)
                }
              />
            </div>
            <div className="label-field-container">
              <FieldLabel label="Phone Number" />
              <div className="partner-form-phone-row">
                <div className="partner-form-phone-row__code">
                  <Dropdown
                    id="countryCodeModal"
                    label=""
                    placeholder="Country Code"
                    options={countryCodeOptions}
                    selected={selectedCountryCode}
                    onSelect={handleCountryCodeSelect}
                    menuPortalTarget={document.body}
                    styles={modalDropdownMenuStyles}
                    isClearable={true}
                  />
                </div>
                <div className="partner-form-phone-row__number">
                  <TextField
                    {...validationState.phoneStatus}
                    id="phoneNumberModal"
                    name="phoneNumber"
                    placeholder="Phone number"
                    value={formData.phoneNumber}
                    theme="dark"
                    helperText={validationState.errorMsg.phoneMsg}
                    onChange={(e) =>
                      handleInputChange("phoneNumber", e.currentTarget.value)
                    }
                    onBlur={handlePhoneBlur}
                  />
                </div>
              </div>
            </div>
            <div className="label-field-container">
              <FieldLabel label="Website" />
              <TextField
                label=""
                id="websiteModal"
                name="website"
                placeholder=""
                value={formData.website}
                theme="dark"
                onChange={(e) =>
                  handleInputChange("website", e.currentTarget.value)
                }
              />
            </div>
          </div>
        </FormSection>

        <FormSection title="Others">
          <div className="partner-form-grid">
            <AccountManagerEmailField
              value={formData.accountManagerEmail}
              onChange={(value) =>
                handleInputChange("accountManagerEmail", value)
              }
              onBlur={handleAccountManagerEmailBlur}
              errorMsg={validationState.errorMsg.accountManagerEmailMsg}
              options={accountManagerEmailOptions}
              variant="modal"
              isLoading={userStatus.userListHIDLoading}
            />
            <div className="label-field-container">
              <FieldLabel label="Partner E-Mail ID" />
              <TextField
                {...validationState.emailStatus}
                helperText={validationState.errorMsg.emailMsg}
                label=""
                id="emailModal"
                name="email"
                placeholder="@organization.com"
                value={formData.email}
                theme="dark"
                onChange={(e) =>
                  handleInputChange("email", e.currentTarget.value)
                }
                onBlur={handleEmailBlur}
              />
            </div>
            <RegistrationDateField
              variant="modal"
              value={formData.registrationDate}
              onChange={(date) => handleDateChange(date, "registrationDate")}
            />
          </div>
        </FormSection>
      </div>
    );
  }

  return <div className="partner-detail-form-container">{formFields}</div>;
};

export default memo(PartnerDetailForm);
